import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { PageHeader } from '../../components/layout';
import ReadFirstRegisterPage from '../readFirst/ReadFirstRegisterPage';

type ContractTabKey =
  | 'contracts'
  | 'instructions'
  | 'variations'
  | 'claims'
  | 'payments';

const tabs: Array<{
  key: ContractTabKey;
  label: string;
  paths: string[];
}> = [
  {
    key: 'contracts',
    label: 'Contracts & Subcontracts',
    paths: ['/contract-admin/contracts', '/contract-admin/subcontracts'],
  },
  {
    key: 'instructions',
    label: 'Instructions & RFIs',
    paths: ['/contract-admin/instructions', '/contract-admin/rfis'],
  },
  {
    key: 'variations',
    label: 'Variations & EOT',
    paths: ['/contract-admin/variations', '/contract-admin/extensions-of-time'],
  },
  {
    key: 'claims',
    label: 'Claims, Notices & Contra-charges',
    paths: ['/contract-admin/claims', '/contract-admin/notices', '/contract-admin/contra-charges'],
  },
  {
    key: 'payments',
    label: 'Payment Apps, Retention & Final Accounts',
    paths: ['/contract-admin/payment-applications', '/contract-admin/retention', '/contract-admin/final-accounts'],
  },
];

const isContractTabKey = (value: string | null): value is ContractTabKey =>
  Boolean(value && tabs.some((tab) => tab.key === value));

export default function ContractAdminWorkspacePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = useMemo(() => {
    const queryTab = new URLSearchParams(location.search).get('tab');
    return isContractTabKey(queryTab) ? queryTab : 'contracts';
  }, [location.search]);
  const [tab, setTab] = useState<ContractTabKey>(initialTab);

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  const active = tabs.find((item) => item.key === tab) ?? tabs[0];

  return (
    <Box>
      <PageHeader
        title="Contract Admin"
        subtitle="Read-first commercial registers consolidated into one workspace"
      />
      <Paper sx={{ borderRadius: 1 }}>
        <Tabs
          value={tab}
          onChange={(_, value: ContractTabKey) => {
            setTab(value);
            navigate(`/contract-admin?tab=${value}`, { replace: true });
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map((item) => (
            <Tab key={item.key} value={item.key} label={item.label} />
          ))}
        </Tabs>
        <Box sx={{ p: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Capture stays on the source workflows in this wave; these tabs preserve the existing read-first registers.
          </Alert>
          {active.paths.map((path) => (
            <Box key={path} sx={{ mb: 3 }}>
              <ReadFirstRegisterPage configPath={path} embedded />
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}
